import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useMutation } from '@tanstack/react-query'

import { FxButton, FxTextField } from '@/components/fx'
import { authApi } from '@/lib/api'
import { changePasswordErrorMessage, validatePasswordChange } from './changePassword'

/**
 * "Passwort ändern", as a dialog on Radix rather than on `FxUploadDialog`:
 * that component is the upload dialog with its own dropzone and footer, and
 * this needs a form with a focus trap and nothing else. The surface follows
 * the design system's dialog — `radius-xl`, `shadow-overlay`, the dimmed
 * page behind it.
 *
 * `onOpenChange` is the only way out, so Escape, the backdrop and Abbrechen
 * all close the same way, and the fields are cleared on close: a password
 * left in a detached React tree is a password still in memory.
 */
export function ChangePasswordDialog({
  open,
  onOpenChange,
  onChanged,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onChanged: () => void
}) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)

  const change = useMutation({
    mutationFn: () => authApi.changePassword(current, next),
    onSuccess: () => {
      reset()
      onOpenChange(false)
      onChanged()
    },
  })

  function reset() {
    setCurrent('')
    setNext('')
    setConfirm('')
    setLocalError(null)
    change.reset()
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    const problem = validatePasswordChange(next, confirm)
    setLocalError(problem)
    if (problem) return
    change.mutate()
  }

  const message = localError ?? (change.isError ? changePasswordErrorMessage(change.error) : null)

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        if (!value) reset()
        onOpenChange(value)
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/55" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(480px,calc(100vw-40px))] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-surface p-10 shadow-overlay"
          aria-describedby={undefined}
        >
          <Dialog.Title className="text-dialog-title text-text">Passwort ändern</Dialog.Title>
          <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-5">
            <FxTextField
              label="Aktuelles Passwort"
              type="password"
              autoComplete="current-password"
              value={current}
              required
              onChange={(event) => setCurrent(event.target.value)}
            />
            <FxTextField
              label="Neues Passwort"
              type="password"
              autoComplete="new-password"
              hint="Mindestens 8 Zeichen."
              value={next}
              required
              onChange={(event) => setNext(event.target.value)}
            />
            <FxTextField
              label="Neues Passwort wiederholen"
              type="password"
              autoComplete="new-password"
              value={confirm}
              required
              onChange={(event) => setConfirm(event.target.value)}
            />
            {/* One message for the whole form: the failures are about the
                combination of fields, not about one of them. */}
            <p aria-live="polite" className="text-small text-danger-text">
              {message}
            </p>
            <div className="flex flex-wrap justify-end gap-4">
              <Dialog.Close asChild>
                <FxButton variant="tonal">Abbrechen</FxButton>
              </Dialog.Close>
              <FxButton type="submit" disabled={change.isPending}>
                {change.isPending ? 'Wird geändert …' : 'Passwort ändern'}
              </FxButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
